/**
 * Reconciliation Service
 *
 * Handles all bank reconciliation API calls.
 */

import api from './api';
import type {
  ApiResponse,
  ReconciliationSession,
  ReconciliationLine,
  ReconciliationMatch,
  CreateReconciliationSessionRequest,
  CreateReconciliationLineRequest,
  Operation,
  PDFExtractionStatus,
  PDFExtractionResult,
} from '@budgira/shared';

/**
 * Response type for session with full data
 */
interface ReconciliationSessionData {
  session: ReconciliationSession;
  lines: ReconciliationLine[];
  operations: Operation[];
  matches: Record<string, ReconciliationMatch[]>;
}

export const reconciliationService = {
  /**
   * Get all reconciliation sessions for the authenticated user
   */
  async listSessions(includeCompleted: boolean = true): Promise<ReconciliationSession[]> {
    const params = includeCompleted ? '' : '?includeCompleted=false';
    const response = await api.get<ApiResponse<ReconciliationSession[]>>(
      `/reconciliation/sessions${params}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch sessions');
    }
    return response.data.data;
  },

  /**
   * Get sessions for a specific account
   */
  async listSessionsByAccount(accountId: string): Promise<ReconciliationSession[]> {
    const response = await api.get<ApiResponse<ReconciliationSession[]>>(
      `/reconciliation/sessions/account/${accountId}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch sessions');
    }
    return response.data.data;
  },

  /**
   * Get a session with all its data (lines, operations, matches)
   */
  async getSessionWithData(sessionId: string): Promise<ReconciliationSessionData> {
    const response = await api.get<ApiResponse<ReconciliationSessionData>>(
      `/reconciliation/sessions/${sessionId}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch session');
    }
    return response.data.data;
  },

  /**
   * Create a new reconciliation session
   */
  async createSession(data: CreateReconciliationSessionRequest): Promise<ReconciliationSession> {
    const response = await api.post<ApiResponse<ReconciliationSession>>(
      '/reconciliation/sessions',
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create session');
    }
    return response.data.data;
  },

  /**
   * Update session date tolerance
   */
  async updateTolerance(
    sessionId: string,
    dateToleranceDays: number
  ): Promise<ReconciliationSession> {
    const response = await api.put<ApiResponse<ReconciliationSession>>(
      `/reconciliation/sessions/${sessionId}/tolerance`,
      { dateToleranceDays }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update tolerance');
    }
    return response.data.data;
  },

  /**
   * Complete a session
   */
  async completeSession(sessionId: string): Promise<ReconciliationSession> {
    const response = await api.post<ApiResponse<ReconciliationSession>>(
      `/reconciliation/sessions/${sessionId}/complete`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to complete session');
    }
    return response.data.data;
  },

  /**
   * Cancel a session
   */
  async cancelSession(sessionId: string): Promise<ReconciliationSession> {
    const response = await api.post<ApiResponse<ReconciliationSession>>(
      `/reconciliation/sessions/${sessionId}/cancel`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to cancel session');
    }
    return response.data.data;
  },

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<void> {
    const response = await api.delete<ApiResponse<null>>(
      `/reconciliation/sessions/${sessionId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete session');
    }
  },

  /**
   * Import CSV lines into a session
   */
  async importLines(
    sessionId: string,
    lines: CreateReconciliationLineRequest[]
  ): Promise<ReconciliationLine[]> {
    const response = await api.post<ApiResponse<ReconciliationLine[]>>(
      `/reconciliation/sessions/${sessionId}/lines`,
      { lines }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to import lines');
    }
    return response.data.data;
  },

  /**
   * Get potential matches for a specific line
   */
  async getLineMatches(
    sessionId: string,
    lineId: string
  ): Promise<ReconciliationMatch[]> {
    const response = await api.get<ApiResponse<ReconciliationMatch[]>>(
      `/reconciliation/sessions/${sessionId}/lines/${lineId}/matches`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get matches');
    }
    return response.data.data;
  },

  /**
   * Match a line with an operation
   */
  async matchLine(
    sessionId: string,
    lineId: string,
    operationId: string,
    pointOperation: boolean = true,
    updateDate: boolean = false,
    counterpartyAlias?: string
  ): Promise<ReconciliationLine> {
    const response = await api.post<ApiResponse<ReconciliationLine>>(
      `/reconciliation/sessions/${sessionId}/lines/${lineId}/match`,
      { operationId, pointOperation, updateDate, counterpartyAlias }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to match line');
    }
    return response.data.data;
  },

  /**
   * Mark a line as having a created operation
   */
  async markLineCreated(
    sessionId: string,
    lineId: string,
    operationId: string
  ): Promise<ReconciliationLine> {
    const response = await api.post<ApiResponse<ReconciliationLine>>(
      `/reconciliation/sessions/${sessionId}/lines/${lineId}/created`,
      { operationId }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to mark line as created');
    }
    return response.data.data;
  },

  /**
   * Ignore a line
   */
  async ignoreLine(sessionId: string, lineId: string): Promise<ReconciliationLine> {
    const response = await api.post<ApiResponse<ReconciliationLine>>(
      `/reconciliation/sessions/${sessionId}/lines/${lineId}/ignore`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to ignore line');
    }
    return response.data.data;
  },

  /**
   * Reset a line to pending status
   */
  async resetLine(sessionId: string, lineId: string): Promise<ReconciliationLine> {
    const response = await api.post<ApiResponse<ReconciliationLine>>(
      `/reconciliation/sessions/${sessionId}/lines/${lineId}/reset`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to reset line');
    }
    return response.data.data;
  },

  /**
   * Check if PDF extraction is available (AI configured)
   */
  async getPDFExtractionStatus(): Promise<PDFExtractionStatus> {
    const response = await api.get<ApiResponse<PDFExtractionStatus>>(
      '/reconciliation/pdf/status'
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get PDF extraction status');
    }
    return response.data.data;
  },

  /**
   * Extract transactions from a PDF bank statement using AI
   */
  async extractPDF(
    pdfContent: string,
    filename?: string,
    bankHint?: string
  ): Promise<PDFExtractionResult> {
    const response = await api.post<ApiResponse<PDFExtractionResult>>(
      '/reconciliation/pdf/extract',
      { pdfContent, filename, bankHint }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to extract PDF');
    }
    return response.data.data;
  },
};

export default reconciliationService;
